import { useState } from "react";
import { useSelector } from "react-redux";

import {
  ITEM_CALCULATED_FIELDS,
  ITEM_CALCULATED_FIELDS_NO_ON_HAND,
} from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLinkedTransactionSelect } from "components/formFields/line/LineLinkedTransactionSelect";
import { LineLotSelect } from "components/formFields/line/LineLotSelect";
import { LineOnHand } from "components/formFields/line/LineOnHand";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsSelect } from "components/formFields/line/LineSerialsSelect";
import { LineUom } from "components/formFields/line/LineUom";
import { LineVolumeAndVolumeUnit } from "components/formFields/line/LineVolumeAndVolumeUnit";
import { LineWeightAndWeightUnit } from "components/formFields/line/LineWeightAndWeightUnit";

import {
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { afterTouchLine } from "services/sosInventoryService/fulfillmentTransaction/afterTouchLine";
import { updateLineWithItem } from "services/sosInventoryService/fulfillmentTransaction/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { formatLotsWithQuantityAndExpiration } from "services/utility/formatting";
import { TYPES } from "services/utility/schema";

import { useItemLocationLots } from "hooks/useItemLocationLots";
import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES, ITEM_QUICKLIST_CASES } from "appConstants";
import { LINE_ITEM_FIELDS } from "editConfig";

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    objectType,
    record,
    location,
    line,
    lineHandler,
    expandItemGroup,
    items,
    itemsCount,
    classes,
    classesCount,
    jobWorkcenters,
    addItem,
    lineItemsErrors: errors,
    showClass,
    lineReadOnly = false,
    handleDeleteLine,
  } = props;
  const { date } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [itemLoading, setItemLoading] = useState(false);
  const { hasAtLeastPlusPlan } = usePlans();

  // SETTINGS
  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );
  const showPickTicketSerialsLots = useSelector(
    (state) => state.userCompanySettings.settings.showPickTicketSerialsLots
  );

  const rawLots = useItemLocationLots(
    {
      itemId: line.item?.id,
      location: record.location?.name,
      binId: line.bin?.id,
    },
    date,
    line.itemDetails.lotTracking
  );

  const lots = rawLots ? formatLotsWithQuantityAndExpiration(rawLots) : null;

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    const lineItemFields = LINE_ITEM_FIELDS[objectType];
    if (lineItemFields[TYPES.reference].includes(fieldName)) {
      newLine = { ...line, [fieldName]: { id: newValue } };
    } else if (lineItemFields[TYPES.decimal].includes(fieldName)) {
      newLine = { ...line, [fieldName]: new Decimal(newValue || 0) };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  function handleReferenceChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      openAddItem(userInput);
      return;
    }
    setItemLoading(true);
    const itemFields = showQuantityOnBuildsAndShipments
      ? ITEM_CALCULATED_FIELDS[objectType]
      : ITEM_CALCULATED_FIELDS_NO_ON_HAND[objectType];
    const item = await getItemRecord(
      newItem.id,
      record.location?.id,
      date,
      itemFields
    );
    const newLine = await updateLineWithItem(item, line, record);
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });
    setItemLoading(false);
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const updatedLine = afterTouchLine({ ...line, uom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  function handleLinkedTransactionChange(_, newValue) {
    const newLine = { ...line, linkedTransaction: newValue };
    setPageDirty();
    lineHandler({ type: "update", updatedLine: newLine });
  }

  async function onAddItem(item) {
    setAddNewItem(false);
    await handleItemChange(item);
    addItem();
  }

  function handleDelete() {
    if (handleDeleteLine) {
      handleDeleteLine(line.lineNumber);
    } else {
      setPageDirty();
      lineHandler({ type: "delete", deleteAt: line.lineNumber });
    }
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  const showSerialsLots =
    objectType !== OBJECT_TYPES.PICK_TICKET.fullString ||
    showPickTicketSerialsLots;

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>
      <LineInventoryItemBin
        allowExpand
        itemLoading={itemLoading}
        objectType={objectType}
        itemFetchCase={ITEM_QUICKLIST_CASES.NON_CATEGORY}
        bins={line.availableBins}
        line={line}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleReferenceChange}
        expandItemGroup={expandItemGroup}
        isInError={isInError}
        location={location}
        lineReadOnly={lineReadOnly}
      />
      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
          lineReadOnly={lineReadOnly}
        />
        {showSerialsLots && hasAtLeastPlusPlan && (
          <>
            {line.itemDetails?.serialTracking && (
              <LineSerialsSelect
                objectType={objectType}
                value={line.serials}
                itemId={line.item.id}
                bin={line.bin}
                location={location}
                quantity={line.quantity}
                onValueChange={handleReferenceChange}
                error={isInError("serials")}
                lineReadOnly={lineReadOnly}
              />
            )}

            {line.itemDetails?.lotTracking && (
              <LineLotSelect
                value={line.lot}
                lots={lots}
                bin={line.bin}
                objectType={objectType}
                location={location}
                onValueChange={handleReferenceChange}
                lineReadOnly={lineReadOnly}
              />
            )}
          </>
        )}
      </LineTableCell>
      {showQuantityOnBuildsAndShipments && (
        <LineOnHand value={line.onhand} lineReadOnly={lineReadOnly} />
      )}
      <LineQuantity
        objectType={objectType}
        value={line.quantity}
        onValueChange={handleValueChange}
        error={isInError("quantity")}
        disabled={itemLoading}
        lineReadOnly={lineReadOnly}
      />
      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
        lineReadOnly={lineReadOnly}
      />
      <LineWeightAndWeightUnit
        weight={line.weight}
        weightunit={line.weightunit}
      />
      <LineVolumeAndVolumeUnit
        volume={line.volume}
        volumeunit={line.volumeunit}
      />
      <LineLinkedTransactionSelect
        objectType={objectType}
        value={line.linkedTransaction}
        onValueChange={handleLinkedTransactionChange}
        error={isInError("linkedTransaction")}
        lineReadOnly={lineReadOnly}
      />
      {showClass && (
        <LineClass
          value={line.class}
          classes={classes}
          classesCount={classesCount}
          onValueChange={handleReferenceChange}
          lineReadOnly={lineReadOnly}
        />
      )}
      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleReferenceChange}
        lineReadOnly={lineReadOnly}
      />
      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
        />
      )}
    </>
  );
}
